import IconDislikeSolid from '@apollo/icons/default/IconDislikeSolid.svg';
import IconLikeSolid from '@apollo/icons/default/IconLikeSolid.svg';
import {
  Admonition,
  AdmonitionBody,
  AdmonitionFooter,
  AdmonitionHeader,
  AdmonitionTitle,
  Button,
  ButtonGroup,
  IconButton,
  Textarea,
  Tooltip,
} from '@apollo/orbit';
import isChromatic from 'chromatic';
import classnames from 'classnames';
import React, { useCallback, useState } from 'react';

import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { assertUnreachableOrReturnDefault } from 'src/lib/assertUnreachable';

import { graphRefToString, useGraphRef } from '../../hooks/useGraphRef';

type Liked = 'Like' | 'Dislike';

/**
 * responses: https://docs.google.com/forms/d/1BnohFwmz0Q0AceWWRGyf-P8JdYClew4PemxQiFlnCbQ/edit#responses
 * responses sheet: https://docs.google.com/spreadsheets/d/1JnqtNdyQImH2726ZEay3OTx3D2THc5NIB8GhbQq3BkQ/edit?resourcekey=&gid=319392154#gid=319392154
 */
function useGoogleForm() {
  const graphRef = useGraphRef();
  const [localStorageIsComplete, setLocalStorageIsComplete] = useLocalStorage(
    'hasCompletedCheckLayoutFeedback',
  );
  const [isComplete, setIsComplete] = useState(localStorageIsComplete);
  const [isFeedbackSubmitting, setIsFeedbackSubmitting] = React.useState(false);
  const submitFeedback = useCallback(
    ({ liked, text }: { liked: Liked; text: string }): Promise<void> => {
      setIsFeedbackSubmitting(true);
      const formData = new FormData();
      formData.append('entry.911887061', liked);
      formData.append('entry.1588089529', text);
      formData.append('entry.813861830', window.location.href);
      if (graphRef) {
        formData.append('entry.230121333', graphRefToString(graphRef));
      }

      if (isChromatic()) {
        setLocalStorageIsComplete(true);
        setIsFeedbackSubmitting(false);
        return Promise.resolve();
      } else {
        return fetch(
          'https://docs.google.com/forms/d/e/1FAIpQLSfXAOiEPEJ_VQyY4v_o4L2DKVmsP14rFJPsxaFzbjf42dsRtQ/formResponse',
          {
            method: 'POST',
            body: formData,
            mode: 'no-cors', // Important for avoiding CORS issues
          },
        )
          .then(() => setLocalStorageIsComplete(true))
          .catch(() => {})
          .then(() => setIsFeedbackSubmitting(false));
      }
    },
    [setLocalStorageIsComplete, graphRef],
  );

  return {
    submitFeedback,
    feedbackState: isFeedbackSubmitting
      ? ('submitting' as const)
      : isComplete
        ? ('hidden' as const)
        : localStorageIsComplete
          ? ('thank-you' as const)
          : ('visible' as const),
    hideCompleteFeedbackForm: () => {
      setIsComplete(localStorageIsComplete);
    },
  };
}

function LikeButtons({
  liked,
  setLiked,
}: {
  liked: Liked | null;
  setLiked: (liked: Liked) => void;
}) {
  return (
    <ButtonGroup>
      <Tooltip label="I love it">
        <IconButton
          data-testid="like"
          className={classnames('group hover:bg-success', {
            '[&_svg]:icon-success !bg-success': liked === 'Like', // TODO: we shouldn't need important
          })}
          size="sm"
          variant="secondary"
          icon={<IconLikeSolid className="group-hover:icon-success" />}
          aria-label="like"
          onClick={() => setLiked('Like')}
          isActive={liked === 'Like'}
        />
      </Tooltip>
      <Tooltip label="I dislike it">
        <IconButton
          data-testid="dislike"
          className={classnames('group hover:bg-error', {
            '[&_svg]:icon-error !bg-error': liked === 'Dislike', // TODO: we shouldn't need important
          })}
          size="sm"
          variant="secondary"
          icon={<IconDislikeSolid className="group-hover:icon-error" />}
          aria-label="dislike"
          onClick={() => setLiked('Dislike')}
          isActive={liked === 'Dislike'}
        />
      </Tooltip>
    </ButtonGroup>
  );
}
export function FeedbackAdmonition() {
  const [liked, setLiked] = useState<Liked | null>(null);
  const { submitFeedback, feedbackState, hideCompleteFeedbackForm } =
    useGoogleForm();
  const [feedbackText, setFeedbackText] = useState('');

  if (feedbackState === 'visible' || feedbackState === 'submitting') {
    return liked === null ? (
      <Admonition variant="neutral">
        <AdmonitionHeader>
          <AdmonitionTitle>Do you like this new layout?</AdmonitionTitle>
        </AdmonitionHeader>
        <AdmonitionBody>
          We’ve revamped the checks UI, let us know what you think.
        </AdmonitionBody>
        <AdmonitionFooter>
          <LikeButtons liked={liked} setLiked={setLiked} />
        </AdmonitionFooter>
      </Admonition>
    ) : (
      <Admonition variant="neutral">
        <AdmonitionHeader>
          <AdmonitionTitle>Do you like this new layout?</AdmonitionTitle>
        </AdmonitionHeader>
        <AdmonitionBody>
          <LikeButtons liked={liked} setLiked={setLiked} />
          <Textarea
            className="mt-4"
            size="md"
            placeholder="Tell us more (optional)"
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.currentTarget.value)}
          />
        </AdmonitionBody>
        <AdmonitionFooter>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => submitFeedback({ liked, text: feedbackText })}
            isLoading={feedbackState === 'submitting'}
            data-testid="submit-feedback"
          >
            Submit
          </Button>
        </AdmonitionFooter>
      </Admonition>
    );
  } else if (feedbackState === 'thank-you') {
    return (
      <Admonition variant="neutral">
        <AdmonitionHeader>
          <AdmonitionTitle>Thanks for your feedback!</AdmonitionTitle>
        </AdmonitionHeader>
        <AdmonitionBody>
          We appreciate your response and will use it to improve our product.
        </AdmonitionBody>
        <AdmonitionFooter>
          <Button
            size="sm"
            variant="secondary"
            onClick={hideCompleteFeedbackForm}
          >
            Done
          </Button>
        </AdmonitionFooter>
      </Admonition>
    );
  } else if (feedbackState === 'hidden') {
    return null;
  } else {
    return assertUnreachableOrReturnDefault(feedbackState, null);
  }
}
